/**
 * The attract-mode serpent.
 *
 * The title screen isn't a still image — it's a real run of the real
 * simulation with this thing at the controls, so the first thing anyone sees
 * is the game actually being played. It is deliberately decent-but-not-great:
 * it chases light, avoids obvious death, and takes the bite when it's cornered.
 */

import type { Dir, SimState, Vec } from './types'
import { DIR_VECTORS, opposite } from './types'
import { bloomOpen } from './sim'

export function botTurn(s: SimState, allowBite: boolean): Dir | null {
  const head = s.snake[0]
  const target = nearestTarget(s, head)

  let best: Dir | null = null
  let bestScore = -Infinity

  for (let d = 0 as Dir; d < 4; d = (d + 1) as Dir) {
    if (d === opposite(s.dir)) continue
    const v = DIR_VECTORS[d]
    const nx = head.x + v.x
    const ny = head.y + v.y
    if (nx < 0 || ny < 0 || nx >= s.grid || ny >= s.grid) continue

    const hitsBody = bodyIndexAt(s, nx, ny)
    if (hitsBody >= 0) {
      const canBite = allowBite && s.biteCooldown === 0 && s.snake.length >= 8
      // Only worth cutting if the severed piece is meaningful.
      if (!canBite || s.snake.length - hitsBody < 6) continue
    }
    if (s.blooms.some((b) => b.x === nx && b.y === ny && bloomOpen(b, s.tick + 1))) continue

    // Room to keep living matters far more than a shortcut to the next mote.
    const room = floodFill(s, nx, ny)
    const dist = target ? Math.abs(nx - target.x) + Math.abs(ny - target.y) : 0
    let score = room * 3 - dist
    if (hitsBody >= 0) score += 40 // an escape hatch, when it's the sane play
    if (score > bestScore) {
      bestScore = score
      best = d
    }
  }

  return best === s.dir ? null : best
}

function nearestTarget(s: SimState, from: Vec): Vec | null {
  let best: Vec | null = null
  let bestD = Infinity
  for (const p of s.pickups) {
    // Chase embers only when they're worth the detour; motes are the job.
    const weight = p.kind === 'mote' ? 0 : p.kind === 'prism' ? 4 : 8
    const d = Math.abs(p.x - from.x) + Math.abs(p.y - from.y) + weight
    if (d < bestD) {
      bestD = d
      best = { x: p.x, y: p.y }
    }
  }
  return best
}

function bodyIndexAt(s: SimState, x: number, y: number): number {
  for (let i = 0; i < s.snake.length - 1; i++) {
    if (s.snake[i].x === x && s.snake[i].y === y) return i
  }
  return -1
}

/** Reachable free cells from a starting point, capped so it stays cheap. */
function floodFill(s: SimState, sx: number, sy: number): number {
  const g = s.grid
  const blocked = new Uint8Array(g * g)
  for (let i = 0; i < s.snake.length - 1; i++) blocked[s.snake[i].y * g + s.snake[i].x] = 1
  for (const b of s.blooms) if (bloomOpen(b, s.tick + 1)) blocked[b.y * g + b.x] = 1

  const seen = new Uint8Array(g * g)
  const queue = [sy * g + sx]
  seen[queue[0]] = 1
  let count = 0
  const CAP = 120
  while (queue.length && count < CAP) {
    const i = queue.pop() as number
    count++
    const x = i % g
    const y = (i / g) | 0
    for (const v of DIR_VECTORS) {
      const nx = x + v.x
      const ny = y + v.y
      if (nx < 0 || ny < 0 || nx >= g || ny >= g) continue
      const ni = ny * g + nx
      if (seen[ni] || blocked[ni]) continue
      seen[ni] = 1
      queue.push(ni)
    }
  }
  return count
}
